import { hkdf } from "@noble/hashes/hkdf.js";
import { sha256 } from "@noble/hashes/sha2.js";
import { concatBytes, randomBytes } from "@noble/hashes/utils.js";
import { base64, base64urlnopad } from "@scure/base";
import { getPublicKey } from "nostr-tools/pure";
import { isValidPublicKey } from "./keys.js";

/**
 * Encrypted copies of member_secret (PLAN.md 4.6). The recovery phrase is the root; these
 * copies are conveniences, both locked by a passkey:
 *
 *   kind       where    wrap key = HKDF(...)         needs the server
 *   prf-pin    device   passkey PRF + PIN            no
 *   oprf-prf   server   passkey PRF + OPRF(k, PIN)   yes (k)
 *
 * There is no PIN-only server copy (PLAN.md 4.11 H2): whoever held it and the OPRF seed could try
 * every PIN offline. Browsers without passkey PRF keep a plain device copy and rely on the phrase.
 *
 * The OPRF output comes from oprf.ts; the passkey PRF output from WebAuthn in the browser.
 * This module only derives the wrap key and runs AES-256-GCM.
 */
export type KeyCopyKind = "prf-pin" | "oprf-prf";

export type KeyCopy = {
  v: 2;
  kind: KeyCopyKind;
  /** Identity held by this copy, lowercase hex. Not secret; lets the server and the UI tell copies apart. */
  pubkey: string;
  /** WebAuthn credential id (unpadded base64url) of the passkey whose PRF locks this copy. */
  credentialId: string;
  /** base64, 32 bytes, HKDF salt */
  salt: string;
  /** base64, 12 bytes */
  iv: string;
  /** base64, AES-256-GCM of the 32-byte secret, including the 16-byte tag */
  blob: string;
};

/** Unlock factors. Which ones are used depends on the copy kind. */
export type UnlockFactors = {
  pin?: string;
  /** Passkey PRF output (WebAuthn prf extension, results.first), 32 bytes. */
  prf?: Uint8Array;
  /** OPRF output for the PIN (oprf.ts finalizePin). */
  oprf?: Uint8Array;
};

export type KeyCopyErrorCode = "INVALID_PIN" | "MISSING_FACTOR" | "INVALID_COPY" | "WRONG_UNLOCK";

export class KeyCopyError extends Error {
  constructor(
    readonly code: KeyCopyErrorCode,
    message: string,
  ) {
    super(message);
    this.name = "KeyCopyError";
  }
}

const REQUIRED: Record<KeyCopyKind, ReadonlyArray<keyof UnlockFactors>> = {
  "prf-pin": ["prf", "pin"],
  "oprf-prf": ["prf", "oprf"],
};

/** Salt for the WebAuthn prf extension (eval.first). Fixed, so the same passkey always yields the same output. */
export const PASSKEY_PRF_SALT: Uint8Array = sha256(new TextEncoder().encode("vrelis.com passkey prf v1"));

const PIN = /^[0-9]{6,32}$/;
export const MIN_PIN_DIGITS = 6;

export function isValidPin(pin: string): boolean {
  return PIN.test(pin);
}

function assertPin(pin: string | undefined): string {
  if (pin === undefined || !isValidPin(pin)) {
    throw new KeyCopyError("INVALID_PIN", `PIN must be ${MIN_PIN_DIGITS} to 32 digits`);
  }
  return pin;
}

const subtle = () => {
  const c = (globalThis as { crypto?: Crypto }).crypto?.subtle;
  if (!c) throw new Error("WebCrypto is not available in this environment");
  return c;
};

const toBuffer = (bytes: Uint8Array): ArrayBuffer => bytes.slice().buffer as ArrayBuffer;

/** Length-prefixed, so (prf, oprf, pin) can never be confused with another split of the same bytes. */
function lengthPrefixed(bytes: Uint8Array): Uint8Array {
  return concatBytes(new Uint8Array([bytes.length >> 8, bytes.length & 0xff]), bytes);
}

/** Header bound to the ciphertext (HKDF info and AES-GCM additional data): changing kind, pubkey or credential breaks it. */
function header(copy: Pick<KeyCopy, "kind" | "pubkey" | "credentialId">): Uint8Array {
  return new TextEncoder().encode(`vrelis key copy v2|${copy.kind}|${copy.pubkey}|${copy.credentialId}`);
}

async function wrapKeyFor(
  copy: Pick<KeyCopy, "kind" | "pubkey" | "credentialId">,
  salt: Uint8Array,
  factors: UnlockFactors,
): Promise<CryptoKey> {
  const need = REQUIRED[copy.kind];
  for (const f of need) {
    if (factors[f] === undefined) throw new KeyCopyError("MISSING_FACTOR", `a ${copy.kind} copy needs ${need.join(" and ")}`);
  }
  const pin = need.includes("pin") ? new TextEncoder().encode(assertPin(factors.pin)) : new Uint8Array(0);
  const prf = need.includes("prf") ? factors.prf! : new Uint8Array(0);
  const oprf = need.includes("oprf") ? factors.oprf! : new Uint8Array(0);
  if (need.includes("prf") && prf.length < 32) throw new KeyCopyError("MISSING_FACTOR", "passkey PRF output must be at least 32 bytes");
  if (need.includes("oprf") && oprf.length < 32) throw new KeyCopyError("MISSING_FACTOR", "OPRF output must be at least 32 bytes");
  const ikm = concatBytes(lengthPrefixed(prf), lengthPrefixed(oprf), lengthPrefixed(pin));
  const key = hkdf(sha256, ikm, salt, header(copy), 32);
  return subtle().importKey("raw", toBuffer(key), { name: "AES-GCM" }, false, ["encrypt", "decrypt"]);
}

export async function wrapKey(
  secretKey: Uint8Array,
  kind: KeyCopyKind,
  factors: UnlockFactors,
  credentialId: string,
): Promise<KeyCopy> {
  if (secretKey.length !== 32) throw new Error("secret key must be 32 bytes");
  if (!(kind in REQUIRED)) throw new KeyCopyError("INVALID_COPY", `unknown kind ${String(kind)}`);
  if (!isCredentialId(credentialId)) throw new KeyCopyError("INVALID_COPY", "credential id must be unpadded base64url");
  const meta = { kind, pubkey: getPublicKey(secretKey), credentialId };
  const salt = randomBytes(32);
  const iv = randomBytes(12);
  const key = await wrapKeyFor(meta, salt, factors);
  const blob = new Uint8Array(
    await subtle().encrypt({ name: "AES-GCM", iv: toBuffer(iv), additionalData: toBuffer(header(meta)) }, key, toBuffer(secretKey)),
  );
  return { v: 2, ...meta, salt: base64.encode(salt), iv: base64.encode(iv), blob: base64.encode(blob) };
}

/** Opens a copy. Wrong PIN, wrong passkey or wrong OPRF key all fail the same way: WRONG_UNLOCK. */
export async function unwrapKey(copy: KeyCopy, factors: UnlockFactors): Promise<Uint8Array> {
  const checked = parseKeyCopy(copy);
  const key = await wrapKeyFor(checked, base64.decode(checked.salt), factors);
  let secret: Uint8Array;
  try {
    secret = new Uint8Array(
      await subtle().decrypt(
        { name: "AES-GCM", iv: toBuffer(base64.decode(checked.iv)), additionalData: toBuffer(header(checked)) },
        key,
        toBuffer(base64.decode(checked.blob)),
      ),
    );
  } catch {
    throw new KeyCopyError("WRONG_UNLOCK", "wrong PIN or passkey, or the copy is damaged");
  }
  if (secret.length !== 32 || getPublicKey(secret) !== checked.pubkey) {
    throw new KeyCopyError("WRONG_UNLOCK", "the copy does not hold the key it claims");
  }
  return secret;
}

/** WebAuthn credential ids are at most 1023 bytes; stored as unpadded base64url. */
function isCredentialId(id: unknown): id is string {
  if (typeof id !== "string" || !/^[A-Za-z0-9_-]{1,1364}$/.test(id)) return false;
  try {
    return base64urlnopad.decode(id).length > 0;
  } catch {
    return false;
  }
}

function decodedLength(b64: unknown): number {
  if (typeof b64 !== "string") return -1;
  try {
    return base64.decode(b64).length;
  } catch {
    return -1;
  }
}

/** Structural check of an untrusted copy (from the server, or a client upload). Returns a copy with only known fields. */
export function parseKeyCopy(input: unknown): KeyCopy {
  const bad = (why: string) => new KeyCopyError("INVALID_COPY", `invalid key copy: ${why}`);
  if (typeof input !== "object" || input === null) throw bad("not an object");
  const c = input as Record<string, unknown>;
  if (c.v !== 2) throw bad("unsupported version");
  if (c.kind !== "prf-pin" && c.kind !== "oprf-prf") throw bad("unknown kind");
  if (typeof c.pubkey !== "string" || !isValidPublicKey(c.pubkey)) throw bad("pubkey");
  if (!isCredentialId(c.credentialId)) throw bad("credentialId");
  if (decodedLength(c.salt) !== 32) throw bad("salt");
  if (decodedLength(c.iv) !== 12) throw bad("iv");
  if (decodedLength(c.blob) !== 48) throw bad("blob");
  return {
    v: 2,
    kind: c.kind,
    pubkey: c.pubkey,
    credentialId: c.credentialId,
    salt: c.salt as string,
    iv: c.iv as string,
    blob: c.blob as string,
  };
}
